/**
 * A drive as it was driven, read back from its breadcrumbs.
 *
 * Every presence report is kept as a point — where the car was, how far along
 * the route, whether it was on it, and the phone's own GPS speed — one every
 * ten seconds or so. This turns that trail into something to look at: each
 * point with the speed limit of the road it was on, and the figures a group
 * compares afterwards. Stored as text by the server (see `traces` in the web
 * schema): "t,lng,lat,along,off,approaching,speed;" per report.
 *
 * Speed is the phone's own measurement, not worked out from positions. When a
 * phone can't tell, the app sends 0 after a moment rather than nothing, so a
 * nought here is "stopped, or didn't know" — it colours as stopped, which is
 * the honest reading of a point nobody can vouch for.
 */

import { cumulativeDistances, haversineMeters } from './geo.js';
import { decodePolyline } from './polyline.js';
import { speedLimitAt } from './speed.js';
import type { LngLat, RouteDoc } from './types.js';

export interface TrailPoint {
  /** When, in epoch seconds. */
  t: number;
  point: LngLat;
  /** Metres along the route. */
  alongMeters: number;
  offRoute: boolean;
  /** On the way to the start rather than on the route. */
  approaching: boolean;
  /** The phone's GPS speed, m/s; null where none was sent. */
  speedMps: number | null;
  /** The limit of the road here, km/h — null off the route, on the way to the start, or unsigned. */
  limitKph: number | null;
}

/** Parse a stored trail. Malformed points are skipped rather than failing the lot. */
export function parseTrail(text: string): Omit<TrailPoint, 'limitKph'>[] {
  const out: Omit<TrailPoint, 'limitKph'>[] = [];
  for (const raw of text.split(';')) {
    if (!raw) continue;
    const [t, lng, lat, along, off, approaching, speed] = raw.split(',');
    const point: LngLat = [Number(lng), Number(lat)];
    if (!Number.isFinite(Number(t)) || !Number.isFinite(point[0]) || !Number.isFinite(point[1])) continue;
    out.push({
      t: Number(t),
      point,
      alongMeters: Number(along) || 0,
      offRoute: off === '1',
      approaching: approaching === '1',
      speedMps: speed === undefined || speed === '' ? null : Number(speed),
    });
  }
  return out.sort((a, b) => a.t - b.t);
}

/**
 * The trail with each point's speed limit.
 *
 * Only where the car was on the route and not still heading to its start: the
 * limits belong to the planned road, and off it nobody knows what road it was.
 */
export function withLimits(
  points: Omit<TrailPoint, 'limitKph'>[],
  route: Pick<RouteDoc, 'geometry' | 'speedLimits'>,
): TrailPoint[] {
  if (!route.speedLimits) return points.map((p) => ({ ...p, limitKph: null }));
  const cumulative = cumulativeDistances(decodePolyline(route.geometry));
  return points.map((p) => ({
    ...p,
    limitKph:
      p.offRoute || p.approaching ? null : speedLimitAt(route.speedLimits, segmentAt(cumulative, p.alongMeters)),
  }));
}

function segmentAt(cumulative: number[], along: number): number {
  let lo = 0;
  let hi = cumulative.length - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (cumulative[mid + 1]! <= along) lo = mid + 1;
    else hi = mid;
  }
  return Math.min(lo, Math.max(0, cumulative.length - 2));
}

export interface TrailStats {
  /** Straight-line distance between consecutive points, while moving. */
  distanceMeters: number;
  /** Time spent above walking pace. */
  movingSeconds: number;
  /** Distance over moving time. */
  averageMps: number | null;
  topSpeedMps: number | null;
  /** Time spent over the limit of the road, where the limit is known. */
  overLimitSeconds: number;
  /** The moment furthest over the limit, if there was one. */
  worstOver: { speedMps: number; limitKph: number; t: number } | null;
  startedAt: number | null;
  endedAt: number | null;
}

/** Above this counts as moving; below it is parked, queuing or GPS wobble. */
const MOVING_MPS = 2;
/** A gap longer than this is a stop or a lost signal, not driving. */
const MAX_GAP_SECONDS = 60;

/**
 * The figures for one driver's trail.
 *
 * Each interval between reports is credited to the speed at its start, and
 * gaps over a minute are left out — a phone that went quiet for a quarter of
 * an hour was not doing its last speed for all of it.
 */
export function trailStats(points: TrailPoint[]): TrailStats {
  let distanceMeters = 0;
  let movingSeconds = 0;
  let overLimitSeconds = 0;
  let topSpeedMps: number | null = null;
  let worstOver: TrailStats['worstOver'] = null;

  for (let i = 0; i < points.length; i++) {
    const p = points[i]!;
    if (p.speedMps !== null && (topSpeedMps === null || p.speedMps > topSpeedMps)) topSpeedMps = p.speedMps;

    if (p.speedMps !== null && p.limitKph !== null) {
      const over = p.speedMps - p.limitKph / 3.6;
      const worst = worstOver ? worstOver.speedMps - worstOver.limitKph / 3.6 : 0;
      if (over > 0 && over > worst) worstOver = { speedMps: p.speedMps, limitKph: p.limitKph, t: p.t };
    }

    const next = points[i + 1];
    if (!next) continue;
    const gap = next.t - p.t;
    if (gap <= 0 || gap > MAX_GAP_SECONDS) continue;
    if (p.speedMps === null || p.speedMps < MOVING_MPS) continue;

    movingSeconds += gap;
    distanceMeters += haversineMeters(p.point, next.point);
    if (p.limitKph !== null && p.speedMps > p.limitKph / 3.6) overLimitSeconds += gap;
  }

  return {
    distanceMeters,
    movingSeconds,
    averageMps: movingSeconds > 0 ? distanceMeters / movingSeconds : null,
    topSpeedMps,
    overLimitSeconds,
    worstOver,
    startedAt: points[0]?.t ?? null,
    endedAt: points.at(-1)?.t ?? null,
  };
}
